import { defaultMomentDateFormat } from '@sit/core';
import { format } from 'date-fns/format';
import { parse } from 'date-fns/parse';
import { parseISO } from 'date-fns/parseISO';
/**
 * Parse a date value coming from the API, tolerating every shape it can take.
 *
 * The approvals endpoints are inconsistent here:
 *  - `entryDate` comes from a `DATEONLY` column -> `"2026-08-21"`.
 *  - `timesheetStartDate` / `startDate` are `timestamptz` values that the services
 *    `as unknown as string`-cast, so at runtime they are `Date` objects that
 *    `res.json()` serialises as ISO-8601 instants.
 *  - Some fixtures/legacy payloads use `"2026-08-20 00:00:00.000 +00:00"`.
 *
 * A fixed format string is not safe here: `Invalid Date` propagates into
 * `format(...)`, which throws `RangeError` during render. Returning `undefined`
 * lets callers fall back.
 *
 * The date-only case stays *local* midnight on purpose, so it round-trips
 * through `format(d, 'MM/dd/yyyy')` to the same calendar day.
 */
export function parseApiDate(value) {
    if (value == null || value === '') {
        return undefined;
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? undefined : value;
    }
    const legacyTimestamp = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{3} [+-]\d{2}:\d{2}$/;
    if (legacyTimestamp.test(value)) {
        const parsed = parse(value, 'yyyy-MM-dd HH:mm:ss.SSS xxx', new Date());
        if (!Number.isNaN(parsed.getTime())) {
            return parsed;
        }
    }
    const iso = parseISO(value);
    return Number.isNaN(iso.getTime()) ? undefined : iso;
}
/**
 * Parse an API calendar day without converting it to the browser's timezone.
 * Approval period dates can be serialized as UTC-midnight timestamps even
 * though they describe days, not instants. Date objects use their UTC day;
 * strings retain the day written by the API. Use parseApiDate for instants.
 */
export function parseApiCalendarDate(value) {
    if (value == null || !parseApiDate(value))
        return undefined;
    const day = value instanceof Date
        ? value.toISOString().slice(0, 10)
        : value.slice(0, 10);
    const parsed = parseISO(day);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}
/**
 * Converts an Intacct and moment date formats (all capitals, like `MM/DD/YYYY`) to a `date-fns` compatible format.
 * Replaces `Y` with `y` and `D` with `d`.
 */
export function toDateFnsFormat(intacctFormat) {
    return intacctFormat
        .replaceAll('Y', 'y')
        .replaceAll('D', 'd');
}
/**
 * Formats a date for Intacct based on the user's date format
 * @param dateOrDateString the date to format
 * @param dateFormat the `dateFormat` from `getUser().data.dateFormat`
 * @param options the `options` from `format`
 * @returns the formatted date
 */
export function formatIntacctDate(dateOrDateString, dateFormat, options) {
    // An example of the `format` parameter is `MM/DD/YYYY`
    // We use `date-fns` to format the date
    const date = typeof dateOrDateString === 'string'
        ? parse(dateOrDateString, 'yyyy-MM-dd', new Date())
        : dateOrDateString;
    return format(date, toDateFnsFormat(dateFormat), options);
}
/**
 * Gets the user's date format in a `date-fns` compatible format.
 * @param userData the user data from `getUser().data`
 * @returns the user's date format in a date-fns compatible input or a fallback if not found/not loaded
 */
export function getUserDateFormat(userData, fallback = defaultMomentDateFormat) {
    return toDateFnsFormat(userData?.dateFormat ?? fallback);
}
//# sourceMappingURL=date.js.map